import { type JSX, useMemo } from 'react';

import { Text } from '@mantine/core';

import { UserAvatar } from '@/components/user/UserAvatar/UserAvatar';
import { ICON_SIZE_SM } from '@/constants/ui';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';

import type {
  OperationHistoryConfig,
  OperationHistoryEvent as OperationHistoryEventType,
} from '../OperationHistory.types';

import { OperationHistoryDiff } from './OperationHistoryDiff';
import styles from './OperationHistoryEvent.module.css';

interface OperationHistoryEventProps<
  TEvent extends OperationHistoryEventType = OperationHistoryEventType,
> {
  event: TEvent;
  config: OperationHistoryConfig<TEvent>;
  isFirst?: boolean;
}

export function OperationHistoryEventRow<
  TEvent extends OperationHistoryEventType = OperationHistoryEventType,
>({ event, config }: OperationHistoryEventProps<TEvent>): JSX.Element {
  const { formatEventTime } = useLocalizedDate();

  const color = config.getEventColor(event);
  const EventIcon = config.getEventIcon(event);
  const summary = config.getEventSummary(event);
  const diffs = config.getEventDiff(event);

  const formattedTime = useMemo(
    () => formatEventTime(event.createdAt),
    [formatEventTime, event.createdAt]
  );

  return (
    <div className={styles.row}>
      <div className={styles.dot} data-event-color={color}>
        <EventIcon size={ICON_SIZE_SM} />
      </div>
      <div className={styles.content}>
        <div className={styles.header}>
          <UserAvatar
            displayName={event.authorName}
            avatarUrl={event.authorAvatarUrl}
            size="xs"
          />
          <Text size="sm" fw={500} className={styles.authorName}>
            {event.authorName}
          </Text>
          <span className={styles.summary}>{summary}</span>
          <Text size="xs" c="dimmed" className={styles.time}>
            {formattedTime}
          </Text>
        </div>
        {diffs && (
          <div className={styles.diffArea}>
            <OperationHistoryDiff diffs={diffs} />
          </div>
        )}
      </div>
    </div>
  );
}
